<template>
    <div class="fill-height absolute">
        <v-card
            class="d-flex align-end flex-column default_card elevation-0 fill-height mx-2 overflow-wrap-anywhere"
            :class="{ 'cursor-default': !dense }"
            :id="cardId"
            :outlined="dense"
            border="sm"
            @click="onClickCard"
        >
            <v-card-text class="px-2 mb-auto fill-height">
                <v-row no-gutters class="fill-height">
                    <v-col cols="12">
                        <v-row no-gutters class="pb-3">
                            <v-col :cols="$vuetify.display.mobile ? '12' : '10'">
                                <div class="text-medium-emphasis font-weight-bold text-h6">
                                    {{ bankAccountName }}
                                </div>
                            </v-col>
                            <v-col
                                v-if="!$vuetify.display.mobile"
                                cols="2"
                                class="pt-2 pr-2 d-flex justify-end align-baseline text-medium-emphasis font-weight-bold"
                            >
                                <v-icon small v-if="$vuetify.display.sm">$fa-university</v-icon>
                                <v-icon v-if="$vuetify.display.md">$fa-university</v-icon>
                                <v-icon large v-if="$vuetify.display.lgAndUp"
                                    >$fa-university</v-icon
                                >
                            </v-col>
                        </v-row>
                        <div v-if="bankAccount.number">Numéro : {{ bankAccount.number }}</div>
                        <div
                            v-if="
                                bankAccount.iban && !bankAccount.iban.startsWith(bankAccount.name)
                            "
                        >
                            IBAN : {{ bankAccount.iban }}
                        </div>
                        <div v-if="bankAccount.externalName">
                            Nom externe : {{ bankAccount.externalName }}
                        </div>
                        <div>
                            <v-icon x-small>$fa-upload</v-icon>
                            {{ getImportOrigin() }}
                            {{ " - " + bankAccount.bankTransactionCount + " transaction(s)" }}
                        </div>
                        <div v-if="bankAccount.importStartDate">
                            <v-icon x-small>$fa-upload</v-icon>
                            Date de reprise :
                            {{ formatDate(bankAccount.importStartDate) }}
                            {{
                                " - " +
                                bankAccount.beforeImportStartDateBTCount +
                                " transaction(s) avant"
                            }}
                        </div>
                    </v-col>
                    <v-col cols="12" class="text-color-red" v-if="hasError || !bankAccount.bic">
                        <div v-if="hasError">
                            {{ bankAccount.currentError }}
                        </div>
                        <div v-if="!bankAccount.bic" class="d-flex">
                            <v-icon class="mr-2" color="red" small> $oss-warning </v-icon>
                            <div>BIC manquant</div>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions v-if="!dense" style="width: 100%">
                <v-btn
                    @click="onClickConnect"
                    :disabled="!canUserEnableBudgetInsight"
                    :loading="isBankConnecting"
                    variant="flat"
                    class="px-4"
                    size="small"
                >
                    {{ bankAccount.currentError ? "Réparer" : "Configurer" }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script lang="ts">
import moment from "moment-timezone";
import { Component, Vue, Prop, toNative } from "vue-facing-decorator";

import { connectBank } from "@/services/bankServices";
import { ConnectedBankAccount } from "@/types/bankAccounts.types";

@Component({})
export class ConnectedBankAccountCard extends Vue {
    @Prop({ required: true }) bankAccount!: ConnectedBankAccount;
    @Prop({ default: false }) dense: boolean;

    isBankConnecting: boolean = false;

    get cardId(): string {
        return "bank-account-card-" + this.bankAccount.id;
    }

    get bankAccountName(): string {
        let name = "";
        if (this.bankAccount.bank?.name) {
            name = this.bankAccount.bank.name + ": ";
        }
        name += this.bankAccount.name;
        return name;
    }

    get hasError(): boolean {
        return this.bankAccount.currentError && this.bankAccount.currentError.length > 0;
    }

    canUserEnableBudgetInsight(): boolean {
        const currentUser = this.$store.state.user;
        return currentUser.profile?.configuration?.bankAccount?.write;
    }

    getImportOrigin(): string {
        return this.bankAccount.budgetInsightId ? "Import automatique" : "Import manuel";
    }

    formatDate(date: Date): string {
        if (!date) {
            return "";
        }
        moment.locale("fr");
        return moment(date).format("DD/MM/YYYY");
    }

    async onClickConnect(): Promise<void> {
        this.isBankConnecting = true;
        await connectBank(this.$apollo, this.bankAccount.company.id);
        this.isBankConnecting = false;
    }

    async onClickCard(): Promise<void> {
        if (this.dense && this.canUserEnableBudgetInsight()) {
            await this.onClickConnect();
        }
    }
}

export default toNative(ConnectedBankAccountCard);
</script>

<style scoped>
.overflow-wrap-anywhere {
    overflow-wrap: anywhere;
}

.cursor-default {
    cursor: default;
}

.text-color-red {
    color: red;
}
</style>
